import { Container, Filter, Rectangle, Renderer, RenderTexture, Sprite, Texture, filters } from "pixi.js"
import { ROOF_UPLIFT_PX } from "game-common/room/ember/two_storey_house"
import { TileSize } from "game-common/models"
import { textureContentBoundsWhenIdle } from "./sprites/texture_content_bounds"
import { findRendererApp } from "./renderer_app"

/**
 * Art a composite may stand in front of. An owner on another storey than the one drawn whole (a storey
 * between a tree and the viewer) gives its `level` and its storey's container (`layer`, offset by that
 * storey's drop); its bounds are already in the surface's pixels. Its restorations are drawn INSIDE that
 * container, so the storeys above cover them just as they cover the owner itself.
 * An `upright` owner (a wall) stands on its bounds' bottom edge: only art standing south of that, in the
 * world, is in front of it. A flat one (a roof, a floor) is covered by whatever rises above its height.
 */
export interface RoofArtwork { owner: Container; bounds: Rectangle[]; level?: number; layer?: Container; upright?: boolean }
export interface ProjectedComposite { source: Sprite; projection?: Container; level?: number }
interface IndexedOwner { owner: Container; hits: IndexedRect[]; level?: number; layer?: Container; upright?: boolean }
interface IndexedRect { bounds: Rectangle; group: IndexedOwner; query: number }
interface Restoration {
    source: Sprite
    projection?: Container
    roof: Container
    // The storey the roof is drawn in, when that is not the surface itself; the copy is drawn there too.
    layer?: Container
    // The cut the copy shows, kept here too: a storey torn down destroys the copies inside it.
    cutTexture?: Texture
    copy: Sprite
    // The restored area in the source texture's orig pixels; it may extend past the artwork.
    crop: Rectangle
    // The art the copy's texture was cut from (the source's, or its baked blur) and the cut's origin.
    from?: Texture
    cutX: number
    cutY: number
}
interface BakedArt { texture: RenderTexture; pad: number; frame: number }
const INDEX_CELL_SIZE = TileSize * 4
const MAX_SPARES = 64

const ids = new WeakMap<object, number>()
let nextId = 0
const idOf = (target: object): number => {
    let id = ids.get(target)
    if (id === undefined) ids.set(target, id = ++nextId)
    return id
}
// Floor filters are retuned in place (blur grows with storey distance), so settings belong in the key.
const filterKey = (filter: Filter): string => {
    if (filter instanceof filters.BlurFilter) return `${idOf(filter)}:${filter.blur}:${filter.quality}:${filter.resolution}`
    if (filter instanceof filters.ColorMatrixFilter) return `${idOf(filter)}:${filter.alpha}:${filter.matrix.join(",")}`
    return `${idOf(filter)}`
}

/** Merge only exact rectangular unions; holes and stepped edges remain clipped. */
const mergeClipRectangles = (rectangles: Rectangle[]): Rectangle[] => {
    const merged: Rectangle[] = []
    for (const rectangle of rectangles) {
        let current = rectangle
        for (let i = 0; i < merged.length;) {
            const other = merged[i]
            const contains = (a: Rectangle, b: Rectangle) =>
                a.x <= b.x && a.y <= b.y && a.right >= b.right && a.bottom >= b.bottom
            const vertical = current.x === other.x && current.width === other.width &&
                current.y <= other.bottom && other.y <= current.bottom
            const horizontal = current.y === other.y && current.height === other.height &&
                current.x <= other.right && other.x <= current.right
            if (vertical || horizontal || contains(current, other) || contains(other, current)) {
                const x = Math.min(current.x, other.x), y = Math.min(current.y, other.y)
                current = new Rectangle(x, y, Math.max(current.right, other.right) - x,
                    Math.max(current.bottom, other.bottom) - y)
                merged.splice(i, 1)
                i = 0
            } else i++
        }
        merged.push(current)
    }
    return merged
}

/** Restore foreground composite artwork only where raised ceiling artwork would cover it.
 * Ground sprites keep their collision depth against walls and actors.
 *
 * Each restoration is a sprite cut to its clip rectangle and given the source's transform, so it
 * batches like ordinary art. Masks cost a batch break each, and pixi falls back to a full stencil
 * clear for any rectangle mask that is off screen; with a copy per storey and wall, those passes
 * grew with every floor climbed. Distant floors are blurred by a filter on their root, so their
 * restorations are cut from the source art baked through that blur once, not filtered per frame. */
/** The content-bounds cache key for art's height under ceilings. */
const CEILING_HEIGHT = "ceiling-height"

export class RoofCompositeOverlap {
    private copies: Restoration[] = []
    private spares: Sprite[] = []
    private groundDepths = new Map<Sprite, number>()
    private cells = new Map<string, IndexedRect[]>()
    private candidates: IndexedOwner[] = []
    private query = 0
    private styleFrame = 0
    private projectionStyles = new Map<Container, { frame: number; shade: [number, number, number]; filters: Filter[] | null; key: string }>()
    private bakedArt = new Map<string, BakedArt>()
    // Art whose height was still being read at the last rebuild, restored at its texture's full height meanwhile.
    private unread = new Set<Texture>()

    constructor(private surface: Container) {}

    /** The restorations drawn now. They are only rebuilt on a full render, so they move with the map. */
    getCopies(): Sprite[] { return this.copies.map(({ copy }) => copy) }
    /** A storey holding restorations, or the art they copy, was torn down, or art's height has been read: rebuild. */
    isStale(): boolean { return this.copies.some(({ copy, source }) => copy.destroyed || source.destroyed) || this.heightsRead() }
    /** Some art restored at its texture's full height has had its height read since: rebuild. */
    heightsRead(): boolean {
        for (const texture of this.unread) if (textureContentBoundsWhenIdle(texture, CEILING_HEIGHT) !== undefined) return true
        return false
    }
    setSurface(surface: Container): void { this.surface = surface }

    /** Release active art before map sprites are recycled, retaining reusable GPU objects. */
    reset(): void {
        for (const [source, depth] of this.groundDepths) if (!source.destroyed) source.zIndex = depth
        this.groundDepths.clear()
        for (const restoration of this.copies) {
            this.releaseCut(restoration)
            if (restoration.copy.destroyed) continue
            restoration.copy.parent?.removeChild(restoration.copy)
            this.spares.push(restoration.copy)
        }
        this.copies.length = 0
        this.unread.clear()
        for (const group of this.candidates) group.hits.length = 0
        this.candidates.length = 0
        this.cells.clear()
        this.projectionStyles.clear()
    }

    /** Final teardown; shared source textures and filters remain owned by their original art. */
    clear(): void {
        this.reset()
        while (this.spares.length) this.spares.pop()!.destroy()
        for (const { texture } of this.bakedArt.values()) texture.destroy(true)
        this.bakedArt.clear()
    }

    private index(roofs: RoofArtwork[]): void {
        const groups = new Map<Container, IndexedOwner>()
        this.query = 0
        for (const { owner, bounds, level, layer, upright } of roofs) {
            let group = groups.get(owner)
            if (!group) groups.set(owner, group = { owner, hits: [], level, layer, upright })
            for (const rect of bounds) {
                if (rect.width <= 0 || rect.height <= 0) continue
                const entry = { bounds: rect, group, query: 0 }
                for (let x = Math.floor(rect.x / INDEX_CELL_SIZE); x < Math.ceil(rect.right / INDEX_CELL_SIZE); x++) {
                    for (let y = Math.floor(rect.y / INDEX_CELL_SIZE); y < Math.ceil(rect.bottom / INDEX_CELL_SIZE); y++) {
                        const key = `${x},${y}`
                        let bucket = this.cells.get(key)
                        if (!bucket) this.cells.set(key, bucket = [])
                        bucket.push(entry)
                    }
                }
            }
        }
    }

    private nearby(left: number, top: number, right: number, bottom: number): IndexedOwner[] {
        for (const group of this.candidates) group.hits.length = 0
        this.candidates.length = 0
        const query = ++this.query
        for (let x = Math.floor(left / INDEX_CELL_SIZE); x < Math.ceil(right / INDEX_CELL_SIZE); x++) {
            for (let y = Math.floor(top / INDEX_CELL_SIZE); y < Math.ceil(bottom / INDEX_CELL_SIZE); y++) {
                const bucket = this.cells.get(`${x},${y}`)
                if (!bucket) continue
                for (const entry of bucket) {
                    if (entry.query === query) continue
                    entry.query = query
                    const rect = entry.bounds
                    if (left >= rect.right || right <= rect.x || top >= rect.bottom || bottom <= rect.y) continue
                    if (!entry.group.hits.length) this.candidates.push(entry.group)
                    entry.group.hits.push(entry)
                }
            }
        }
        return this.candidates
    }

    rebuild(composites: (Sprite | ProjectedComposite)[], roofs: RoofArtwork[]): void {
        this.reset()
        if (composites.length && roofs.length) this.index(roofs)
        for (const composite of composites) {
            if (!this.cells.size) break
            const { source, projection, level } = composite instanceof Sprite
                ? { source: composite, projection: undefined, level: undefined } : composite
            if (!projection) {
                const groundX = source.x + source.width / 2
                const groundY = source.zIndex - 100 - ROOF_UPLIFT_PX
                const bucket = this.cells.get(`${Math.floor(groundX / INDEX_CELL_SIZE)},${Math.floor(groundY / INDEX_CELL_SIZE)}`)
                if (bucket) for (const { bounds: rect, group: { owner } } of bucket) {
                    if (owner.zIndex < 100) continue // Floor tilemaps are ground artwork, not ceilings.
                    if (groundX >= rect.x && groundX < rect.right && groundY >= rect.y && groundY < rect.bottom) {
                        if (!this.groundDepths.has(source)) this.groundDepths.set(source, source.zIndex)
                        source.zIndex = Math.min(source.zIndex, owner.zIndex - 0.1)
                    }
                }
            }
            const x = source.x + (projection?.x ?? 0), y = source.y + (projection?.y ?? 0)
            const bottom = y + source.height
            const groups = this.nearby(x, y, x + source.width, bottom)
            if (!groups.length) continue // No texture readback for art away from ceilings.
            const depth = projection?.zIndex ?? source.zIndex
            if (!groups.some(group => depth < group.owner.zIndex)) continue
            // Atlas padding does not make an object tall. Read without waiting on the GPU: a readback here
            // stalled the climb onto a storey for as long as the GPU took to drain (half a second, two on a
            // player's machine). Until it lands the art counts at its full height, padding and all.
            const content = textureContentBoundsWhenIdle(source.texture, CEILING_HEIGHT)
            if (content === undefined) this.unread.add(source.texture)
            const height = content ? content.height * Math.abs(source.scale.y) : source.height
            const artBottom = y + (content ? content.bottom * Math.abs(source.scale.y) : source.height)
            for (const { owner, hits, level: ownerLevel, layer, upright } of groups) {
                if (depth >= owner.zIndex) continue
                // A storey's own art already sorts with its walls; only a storey above it can cover it.
                if (level !== undefined && ownerLevel !== undefined && level >= ownerLevel) continue
                // A lower storey's art can only rise in front of artwork above it by its own height: the drop
                // between the two storeys, so a tree four floors down never reaches. Every storey in between
                // counts, or a tree in front of a tall wall shows its top over the upper storeys while the
                // storey between them hides its middle.
                const lift = Math.max(Math.abs(ROOF_UPLIFT_PX), (projection?.y ?? 0) - (layer?.y ?? 0))
                if (height <= lift) continue
                const elevatedBottom = artBottom - lift
                // Across storeys, "lower on screen" is not "in front": a storey further down is drawn further down,
                // so a tree up to a storey's drop BEHIND a wall passes that test. Against a wall it must stand
                // south of the wall's foot in the world. A flat roof or floor needs no such test: on any line of
                // sight the higher point is the nearer, and the lift above keeps only what rises over its height.
                const worldBottom = bottom - (projection?.y ?? 0)
                const standsBehind = (rect: Rectangle) => !!upright && worldBottom <= rect.bottom - (layer?.y ?? 0)
                const clips: Rectangle[] = []
                for (const { bounds: rect } of hits) {
                    const top = Math.max(rect.y, y), end = Math.min(rect.bottom, elevatedBottom)
                    if (bottom <= rect.bottom || end <= top) continue
                    if (standsBehind(rect)) continue
                    clips.push(new Rectangle(rect.x, top, rect.width, end - top))
                }
                for (const rect of mergeClipRectangles(clips)) {
                    const crop = this.textureArea(source, x, y, rect)
                    if (!crop) continue
                    const copy = this.spares.pop() ?? new Sprite()
                    copy.interactive = false
                    // Art from a storey below, standing in front: it stays in the focus of its own storey, not
                    // the out-of-focus storey it is restored into, where blurred it swam over its sharp original.
                    ;(copy as Sprite & { focusBlurExempt?: boolean }).focusBlurExempt = true
                    ;(layer ?? this.surface).addChild(copy)
                    this.copies.push({ source, projection, roof: owner, layer, copy, crop, cutX: 0, cutY: 0 })
                }
            }
        }
        // Keep reuse bounded when moving from a dense scene into a sparse one.
        while (this.spares.length > MAX_SPARES) this.spares.pop()!.destroy()
        this.sync()
    }

    /** A surface rectangle in the orig pixels of the source's texture, ignoring sway skew. */
    private textureArea(source: Sprite, x: number, y: number, rect: Rectangle): Rectangle | undefined {
        const { width, height } = source.texture.orig
        const { x: scaleX, y: scaleY } = source.scale
        if (!width || !height || !scaleX || !scaleY) return
        const originX = source.anchor.x * width, originY = source.anchor.y * height
        const left = (rect.x - x) / scaleX + originX, right = (rect.right - x) / scaleX + originX
        const top = (rect.y - y) / scaleY + originY, bottom = (rect.bottom - y) / scaleY + originY
        return new Rectangle(Math.min(left, right), Math.min(top, bottom), Math.abs(right - left), Math.abs(bottom - top))
    }

    /** Point the copy at the crop of `from`, whose frame starts `pad` pixels before the source's. */
    private cut(restoration: Restoration, from: Texture, pad: number): void {
        this.releaseCut(restoration)
        restoration.from = from
        const { crop, copy } = restoration
        if (copy.destroyed) return
        const { frame } = from
        const left = (from.trim?.x ?? 0) - pad, top = (from.trim?.y ?? 0) - pad
        const x1 = Math.max(crop.x, left), y1 = Math.max(crop.y, top)
        const x2 = Math.min(crop.right, left + frame.width), y2 = Math.min(crop.bottom, top + frame.height)
        if (x2 <= x1 || y2 <= y1) return
        copy.texture = restoration.cutTexture =
            new Texture(from.baseTexture, new Rectangle(frame.x + x1 - left, frame.y + y1 - top, x2 - x1, y2 - y1))
        restoration.cutX = x1
        restoration.cutY = y1
    }

    private releaseCut(restoration: Restoration): void {
        const { copy, cutTexture } = restoration
        if (!copy.destroyed) copy.texture = Texture.EMPTY
        restoration.from = undefined
        restoration.cutTexture = undefined
        cutTexture?.destroy()
    }

    /** The source art rendered through a floor's remaining filters, cached until a sync stops using it. */
    private filteredArt(texture: Texture, stack: Filter[], stackKey: string, frame: number): BakedArt | undefined {
        const key = `${idOf(texture)}|${stackKey}`
        let baked = this.bakedArt.get(key)
        if (!baked) {
            // Headless tests have no renderer; their copies stay unfiltered.
            const renderer = findRendererApp()?.renderer as Renderer | undefined
            if (!renderer || !texture.valid) return
            const pad = Math.ceil(Math.max(0, ...stack.map(filter => filter.padding)))
            const { width, height } = texture.orig
            const art = new Sprite(texture)
            art.filters = stack
            const region = new Rectangle(-pad, -pad, width + pad * 2, height + pad * 2)
            const result = renderer.generateTexture(art, { region, resolution: texture.baseTexture.resolution })
            art.filters = null
            art.destroy()
            this.bakedArt.set(key, baked = { texture: result, pad, frame })
        }
        baked.frame = frame
        return baked
    }

    sync(): void {
        const frame = ++this.styleFrame
        for (const restoration of this.copies) {
            const { source, projection, roof, layer, copy } = restoration
            if (copy.destroyed) continue
            if (source.destroyed) { copy.visible = false; continue }
            const style = projection && this.projectionStyle(projection, frame)
            const filtered = style?.filters ? this.filteredArt(source.texture, style.filters, style.key, frame) : undefined
            const from = filtered?.texture ?? source.texture
            if (restoration.from !== from) this.cut(restoration, from, filtered?.pad ?? 0)
            const x = source.x + (projection?.x ?? 0) - (layer?.x ?? 0), y = source.y + (projection?.y ?? 0) - (layer?.y ?? 0)
            if (copy.x !== x || copy.y !== y) copy.position.set(x, y)
            if (copy.scale.x !== source.scale.x || copy.scale.y !== source.scale.y) copy.scale.copyFrom(source.scale)
            if (copy.skew.x !== source.skew.x || copy.skew.y !== source.skew.y) copy.skew.copyFrom(source.skew)
            if (copy.rotation !== source.rotation) copy.rotation = source.rotation
            // With the same transform, this anchor lays the cut exactly over its place in the source.
            const { width, height } = copy.texture.frame
            const anchorX = width ? (source.anchor.x * source.texture.orig.width - restoration.cutX) / width : 0
            const anchorY = height ? (source.anchor.y * source.texture.orig.height - restoration.cutY) / height : 0
            if (copy.anchor.x !== anchorX || copy.anchor.y !== anchorY) copy.anchor.set(anchorX, anchorY)
            // Inside another storey the copy also takes that storey's own shade: tint only what remains.
            const host = layer ? this.projectionStyle(layer, frame).shade : [1, 1, 1]
            const [r, g, b] = (style ? style.shade : [1, 1, 1]).map((shade, i) => Math.min(1, shade / (host[i] || 1)))
            const tint = r === 1 && g === 1 && b === 1 ? source.tint :
                (Math.round(((source.tint >>> 16) & 255) * r) << 16) |
                (Math.round(((source.tint >>> 8) & 255) * g) << 8) |
                Math.round((source.tint & 255) * b)
            if (copy.tint !== tint) copy.tint = tint
            const depth = roof.zIndex + 0.1
            if (copy.zIndex !== depth) copy.zIndex = depth
            const visible = copy.texture !== Texture.EMPTY && source.visible && (projection?.visible ?? true) &&
                roof.visible && (projection?.zIndex ?? source.zIndex) < roof.zIndex
            if (copy.visible !== visible) copy.visible = visible
            // Avoid doubling the composite's opacity while the roof fades away.
            const alpha = source.alpha * (projection?.alpha ?? 1), coverage = roof.alpha * (layer?.alpha ?? 1)
            const shown = alpha * coverage / Math.max(0.0001, 1 - alpha * (1 - coverage))
            // A copy inside a storey fades with it already.
            const opacity = layer ? Math.min(1, shown / Math.max(0.0001, layer.alpha)) : shown
            if (copy.alpha !== opacity) copy.alpha = opacity
        }
        // Retire art no restoration was cut from this frame, e.g. after a storey change retunes the blur.
        for (const [key, baked] of this.bakedArt) {
            if (baked.frame === frame) continue
            baked.texture.destroy(true)
            this.bakedArt.delete(key)
        }
    }

    /** Darkening each channel by a plain factor is a sprite tint, without baking the shade into the art. */
    private projectionStyle(projection: Container, frame: number) {
        let style = this.projectionStyles.get(projection)
        if (style?.frame === frame) return style
        if (!style) this.projectionStyles.set(projection, style = { frame: 0, shade: [1, 1, 1], filters: null, key: "" })
        const shade: [number, number, number] = [1, 1, 1]
        const remaining: Filter[] = []
        for (const filter of projection.filters ?? []) {
            if (filter instanceof filters.ColorMatrixFilter && filter.alpha === 1) {
                const matrix = filter.matrix
                const scale = matrix.every((value, index) => index === 0 || index === 6 || index === 12
                    ? value >= 0 && value <= 1 : value === (index === 18 ? 1 : 0))
                if (scale) {
                    shade[0] *= matrix[0]
                    shade[1] *= matrix[6]
                    shade[2] *= matrix[12]
                    continue
                }
            }
            remaining.push(filter)
        }
        if (remaining.length !== (style.filters?.length ?? 0) ||
            remaining.some((filter, index) => filter !== style.filters?.[index])) style.filters = remaining.length ? remaining : null
        style.key = style.filters ? style.filters.map(filterKey).join("|") : ""
        style.shade = shade
        style.frame = frame
        return style
    }
}
